import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { Link } from 'react-router-dom';
import { 
  FaFileAlt, FaEye, FaCheckCircle, FaTimes, FaClock,
  FaDownload, FaFilter, FaSearch, FaArrowLeft
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

function AdminUploads({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState({});

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchUploads();
  }, []);

  const fetchUploads = async () => {
    setLoading(true);
    try {
      // Get all uploads
      const response = await fetch(`${API_URL}/subbie/uploads/all`);
      const data = await response.json();
      if (data.success) {
        setUploads(data.uploads || []);
      } else {
        // If the /all endpoint doesn't exist, try to get from subbies
        console.log('Using fallback for uploads');
        setUploads([]);
      }
    } catch (err) {
      console.error('Error fetching uploads:', err);
      setUploads([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'verified': 'bg-green-100 text-green-600',
      'rejected': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'pod': 'POD',
      'weighbridge': 'Weighbridge',
      'fuel': 'Fuel Receipt',
      'check_sheet': 'Check Sheet',
      'driver_hours': 'Driver Hours'
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredUploads = uploads.filter(u => {
    const matchesSearch = 
      (u.jobs?.job_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.subcontractors?.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.upload_type || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || u.status === filter;
    return matchesSearch && matchesFilter;
  });

  const pendingCount = uploads.filter(u => u.status === 'pending').length;

  if (loading) {
    return (
      <AdminLayout activeTab="uploads" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading uploads...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="uploads" onLogout={onLogout} user={user}>
      <div className="flex items-center gap-4 mb-6">
        <Link to="/dashboard" className="p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: '#0A1929' }}>
          <FaArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>📄 Subbie Uploads</h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {pendingCount} pending verification • {uploads.length} total uploads
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-400" />
            <span className="text-sm text-gray-600">Status:</span>
          </div>
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-full text-sm ${filter === 'all' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            All ({uploads.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-1 rounded-full text-sm ${filter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setFilter('verified')}
            className={`px-3 py-1 rounded-full text-sm ${filter === 'verified' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Verified
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-3 py-1 rounded-full text-sm ${filter === 'rejected' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Rejected
          </button>
          <div className="flex-1"></div>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              placeholder="Search uploads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-green w-64"
              style={{ borderColor: '#E8ECEF' }}
            />
          </div>
        </div>
      </div>

      {filteredUploads.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">📤</div>
          <h3 className="text-xl font-semibold" style={{ color: '#0A1929' }}>No Uploads Found</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>Subcontractors haven't uploaded any documents yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredUploads.map((upload) => (
            <div key={upload.id} className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <FaFileAlt className="text-blue-500" />
                    <span className="font-medium" style={{ color: '#0A1929' }}>
                      {getTypeLabel(upload.upload_type)}
                    </span>
                    <span className={getStatusBadge(upload.status)}>
                      {upload.status || 'pending'}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: '#4A5568' }}>
                    <strong>Job:</strong> {upload.jobs?.job_number || 'N/A'} • 
                    <strong> Subbie:</strong> {upload.subcontractors?.company_name || 'N/A'} • 
                    <strong> Volume:</strong> {upload.volume_mt || upload.verified_volume_mt || 0} MT
                  </p>
                  {upload.notes && (
                    <p className="text-sm mt-1" style={{ color: '#4A5568' }}>
                      <span className="font-medium">Notes:</span> {upload.notes}
                    </p>
                  )}
                  <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                    Uploaded: {formatDate(upload.created_at)}
                    {upload.verified_at && ` • Verified: ${formatDate(upload.verified_at)}`}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {upload.status === 'pending' && (
                    <Link
                      to={`/admin/verify-upload/${upload.id}`}
                      className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg text-sm hover:bg-green transition-colors"
                    >
                      <FaEye size={14} /> Verify
                    </Link>
                  )}
                  {upload.file_url && (
                    <button className="flex items-center gap-2 px-3 py-1 border rounded-lg text-sm hover:border-blue-500 hover:text-blue-500 transition-colors" style={{ borderColor: '#E8ECEF' }}>
                      <FaDownload size={14} /> Download
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminUploads;
